/**
 * Sources, accès et campagne de collecte : le bandeau du mode à blanc (tant
 * que le dépôt dans le Drive n'est pas activé), le bouton qui lance une
 * campagne, et l'état de chaque source avec le bilan de la dernière campagne.
 * Une campagne se lance en un instant et se poursuit sur le serveur : l'écran
 * l'interroge toutes les quelques secondes jusqu'à sa fin.
 */

import { entier, horodatageFr } from '@scc/core';
import { h, vider } from '../../dom.js';
import { ErreurPiece, lancerCampagne, sourcesCollecte, type BilanCampagne, type EtatSources, type NaturePiece } from './api.js';
import { brut, creerSection, messageDe, table, type Contexte, type NomSection, type Section } from './communs.js';
import { NATURES, ignoreesEnMots } from './pieces-textes.js';

const INTERVALLE_MS = 3000;

export interface Sources {
  /** Bandeau du mode à blanc et bouton de campagne, en tête de l'onglet. */
  readonly entete: HTMLElement;
  readonly section: Section;
  charger(): Promise<void>;
}

const ACCES: readonly { cle: keyof EtatSources['acces']; libelle: string }[] = [
  { cle: 'freescout', libelle: 'FreeScout' },
  { cle: 'gmail', libelle: 'Gmail' },
  { cle: 'drive', libelle: 'Drive' },
  { cle: 'jev', libelle: 'Jev' },
];

function resumeBilan(b: BilanCampagne): HTMLElement {
  const natures = (Object.keys(NATURES) as NaturePiece[])
    .filter((n) => (b.parNature[n] ?? 0) > 0)
    .map((n) => `${NATURES[n].toLowerCase()} : ${entier(b.parNature[n])}`);
  const ignorees = ignoreesEnMots(b.ignorees);
  return h(
    'div',
    { classe: 'card' },
    h('h3', {}, `Dernière campagne terminée le ${horodatageFr(b.le)}`),
    h(
      'ul',
      { classe: 'exemples-regle' },
      h('li', {}, `${entier(b.messages)} message${b.messages > 1 ? 's' : ''} lu${b.messages > 1 ? 's' : ''}, ${entier(b.pieces)} fichier${b.pieces > 1 ? 's' : ''} candidat${b.pieces > 1 ? 's' : ''}`),
      h('li', {}, `${entier(b.nouvelles)} nouvelle${b.nouvelles > 1 ? 's' : ''} pièce${b.nouvelles > 1 ? 's' : ''}, ${entier(b.doublons)} doublon${b.doublons > 1 ? 's' : ''}, ${entier(b.dejaClassees)} déjà classée${b.dejaClassees > 1 ? 's' : ''} dans le Drive`),
      natures.length > 0 ? h('li', {}, `Triées : ${natures.join(', ')}`) : null,
      h('li', {}, `${entier(b.aTelecharger)} facture${b.aTelecharger > 1 ? 's' : ''} à télécharger, ${entier(b.deposees)} déposée${b.deposees > 1 ? 's' : ''} sans validation`),
      (b.reprises ?? 0) > 0
        ? h('li', {}, `${entier(b.reprises ?? 0)} pièce${(b.reprises ?? 0) > 1 ? 's' : ''} en attente triée${(b.reprises ?? 0) > 1 ? 's' : ''} à cette campagne`)
        : null,
      (b.ecarteesJev ?? 0) > 0
        ? h('li', {}, `${entier(b.ecarteesJev ?? 0)} pièce${(b.ecarteesJev ?? 0) > 1 ? 's' : ''} écartée${(b.ecarteesJev ?? 0) > 1 ? 's' : ''} par Jev, à relire dans l’historique`)
        : null,
      ignorees === null ? null : h('li', {}, ignorees),
    ),
    b.erreurs.length === 0
      ? h('p', { classe: 'note-lecture' }, 'Aucune erreur.')
      : h(
          'div',
          {},
          h('p', {}, h('strong', {}, `${entier(b.erreurs.length)} erreur${b.erreurs.length > 1 ? 's' : ''}`), ' (la campagne a continué avec le reste) :'),
          h('ul', { classe: 'exemples-regle' }, ...b.erreurs.map((e) => h('li', {}, brut(`${e.source} : ${e.message}`)))),
        ),
  );
}

export function creerSources(ctx: Contexte): Sources {
  const section = creerSection(7, 'Sources', 'Les boîtes lues, leurs accès et la dernière campagne');
  const bandeau = h('div', { 'aria-live': 'polite' });
  const bouton = h('button', { type: 'button', classe: 'btn-action principal' }, 'Lancer une campagne de collecte');
  const actualiser = h('button', { type: 'button', classe: 'btn-action' }, 'Actualiser');
  const etatCampagne = h('span', { classe: 'note-lecture', role: 'status' });
  const corps = h('div', {});
  let attente: number | undefined;
  /** Vrai dès qu'une campagne est lancée ou vue en cours, jusqu'à ce qu'on la voie terminée. */
  let suivie = false;

  const entete = h(
    'div',
    {},
    bandeau,
    h('div', { classe: 'barre-actions' }, bouton, actualiser, etatCampagne),
  );

  const dessiner = (e: EtatSources): void => {
    vider(bandeau);
    if (!e.depotActif) {
      bandeau.appendChild(
        h(
          'div',
          { classe: 'insight warning', role: 'note' },
          h('span', { classe: 'insight-icon', 'aria-hidden': 'true' }, '!'),
          h(
            'p',
            {},
            h('strong', {}, 'Mode à blanc : '),
            'les pièces sont triées et proposées, rien n’est déposé dans le Drive. ',
            '« Valider et déposer » le rappellera sans rien déposer.',
          ),
        ),
      );
    }
    bouton.disabled = e.campagne.enCours;
    etatCampagne.textContent = e.campagne.enCours ? 'Campagne en cours… les listes se mettront à jour toutes seules.' : '';

    vider(corps);
    corps.appendChild(
      h(
        'p',
        { classe: 'acces-collecte' },
        h('strong', {}, 'Accès : '),
        ...ACCES.flatMap((a) => [
          h('span', { classe: e.acces[a.cle] ? 'badge b-green' : 'badge b-amber' }, `${a.libelle} ${e.acces[a.cle] ? 'prêt' : 'non configuré'}`),
          ' ',
        ]),
      ),
    );
    if (e.erreursReglage.length > 0) {
      corps.appendChild(
        h(
          'div',
          { classe: 'insight warning' },
          h('span', { classe: 'insight-icon', 'aria-hidden': 'true' }, '!'),
          h('p', {}, 'Le réglage des sources contient des erreurs : ', brut(e.erreursReglage.map((x) => `${x.source} (${x.message})`).join(' ; ')), '.'),
        ),
      );
    }
    corps.appendChild(
      e.sources.length === 0
        ? h('p', { classe: 'note-lecture' }, 'Aucune source dans le réglage du module.')
        : table(
            'Sources de la collecte',
            ['Source', 'Accès', 'Dernière campagne', 'Lu jusqu’au', 'État'],
            e.sources.map((s) =>
              h(
                'tr',
                { classe: s.dansReglage ? null : 'regle-inactive' },
                h('td', { classe: 'operation' }, h('strong', {}, brut(s.libelle)), h('div', { classe: 'note-lecture' }, s.dansReglage ? (s.type === 'freescout' ? 'FreeScout' : 'Gmail') : 'Retirée du réglage')),
                h('td', {}, s.accessible ? 'Prêt' : 'Non configuré'),
                h('td', {}, s.derniereCampagne === null ? 'Jamais' : horodatageFr(s.derniereCampagne)),
                h('td', {}, s.curseur === null ? '—' : horodatageFr(s.curseur)),
                h('td', { classe: 'operation' }, brut(s.dernierEtat ?? '—'), s.derniereErreur === null ? null : h('div', { classe: 'note-lecture erreur-source' }, brut(s.derniereErreur))),
              ),
            ),
          ),
    );
    corps.appendChild(e.campagne.derniere === null ? h('p', { classe: 'note-lecture' }, 'Aucune campagne terminée pour l’instant.') : resumeBilan(e.campagne.derniere));
  };

  /** Relit l'état ; si la campagne suivie vient de finir, relit aussi toutes les listes. */
  const charger = async (): Promise<void> => {
    const e = await sourcesCollecte();
    dessiner(e);
    if (e.campagne.enCours) {
      suivre();
    } else if (suivie) {
      suivie = false;
      const tout: NomSection[] = ['valider', 'arbitrer', 'telecharger', 'historique', 'regles'];
      ctx.recharger(...tout);
      const b = e.campagne.derniere;
      ctx.retour.ok(
        b === null ? 'Campagne terminée.' : `Campagne terminée : ${entier(b.nouvelles)} nouvelle${b.nouvelles > 1 ? 's' : ''} pièce${b.nouvelles > 1 ? 's' : ''}, ${entier(b.erreurs.length)} erreur${b.erreurs.length > 1 ? 's' : ''}.`,
      );
    }
  };

  /** Interroge le serveur jusqu'à la fin de la campagne ; s'arrête si l'onglet a quitté la page. */
  function suivre(): void {
    suivie = true;
    window.clearTimeout(attente);
    attente = window.setTimeout(() => {
      if (!entete.isConnected) return;
      charger().catch(() => {
        // Une lecture ratée ne doit pas arrêter le suivi.
        suivre();
      });
    }, INTERVALLE_MS);
  }

  bouton.addEventListener('click', () => {
    bouton.disabled = true;
    ctx.retour.effacer();
    void (async () => {
      try {
        await lancerCampagne();
        etatCampagne.textContent = 'Campagne lancée… les listes se mettront à jour toutes seules.';
        suivre();
      } catch (e) {
        if (e instanceof ErreurPiece && e.statut === 409) {
          // Une autre campagne tourne déjà : on la suit au lieu d'en lancer une seconde.
          ctx.retour.ok('Une campagne est déjà en cours : l’écran la suit.');
          suivre();
        } else {
          bouton.disabled = false;
          ctx.retour.ko(messageDe(e, 'La campagne n’a pas pu être lancée.'));
        }
      }
    })();
  });
  actualiser.addEventListener('click', () => {
    ctx.recharger('valider', 'arbitrer', 'telecharger', 'historique', 'regles', 'sources');
  });

  section.corps.appendChild(corps);
  return { entete, section, charger };
}
